// src/layout/builders/Payment/PaymentValue.jsx
import React, { useState, useEffect, useMemo } from "react";
import { FaEye } from "react-icons/fa";
import GenericModal from "../../../components/common/GenericModal";
import {
  PlusCircle,
  MinusCircle,
  Send,
  IndianRupee,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import toast from "react-hot-toast";
import usePaymentStore from "../../../store/builders/usePaymentStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const formatDisplayDate = (isoDateString) => {
  if (!isoDateString) return "N/A";
  try {
    const formatted_date = new Date(isoDateString)
      .toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
    return formatted_date;
  } catch (e) {
    return "Invalid Date";
  }
};

const formatCurrency = (amount) => {
  if (amount == null || isNaN(amount)) return "N/A";
  return parseFloat(amount).toLocaleString("en-IN");
};

const statusDisplayInfo = (status) => {
  switch (status) {
    case "Approved":
      return { text: "text-green-700", bg: "bg-green-100" };
    case "Pending":
      return { text: "text-yellow-700", bg: "bg-yellow-100" };
    case "Rejected": // This should handle "Reject" as well
    case "Reject":
      return { text: "text-red-700", bg: "bg-red-100" };
    default:
      return { text: "text-gray-700", bg: "bg-gray-100" };
  }
};

const PaymentValue = ({ projectId }) => {
  const {
    projectValueEntries,
    isValueEntriesLoading,
    isValueEntrySubmitting,
    fetchProjectValueEntries,
    addProjectValueEntry,
  } = usePaymentStore();

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");

  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [changeType, setChangeType] = useState("increase");
  const [changeData, setChangeData] = useState({
    changeAmount: "",
    comment: "",
  });

  useEffect(() => {
    if (projectId) {
      fetchProjectValueEntries(projectId);
    }
  }, [projectId, fetchProjectValueEntries]);

  const isLatestEntryPending = useMemo(() => {
    if (projectValueEntries && projectValueEntries.length > 0) {
      return projectValueEntries[0].status === "Pending";
    }
    return false;
  }, [projectValueEntries]);

  const latestProjectValue = useMemo(() => {
    const latestApprovedEntry = projectValueEntries.find(
      (entry) => entry.status === "Approved"
    );
    if (latestApprovedEntry) {
      return parseFloat(
        latestApprovedEntry.amount?.toString().replace(/,/g, "") || "0"
      );
    }
    return 0;
  }, [projectValueEntries]);

  const handleViewComment = (comment) => {
    setSelectedComment(comment);
    setIsCommentModalOpen(true);
  };

  const handleOpenChangeModal = (type) => {
    if (isLatestEntryPending) {
      toast.error(
        "Please wait for the customer to approve or reject the current pending change."
      );
      return;
    }
    setChangeType(type);
    setChangeData({ changeAmount: "", comment: "" });
    setIsChangeModalOpen(true);
  };

  const handleNewChange = (e) => {
    const { name, value } = e.target;
    if (name === "changeAmount") {
      setChangeData((prev) => ({
        ...prev,
        [name]: value.replace(/[^0-9.]/g, ""),
      }));
    } else {
      setChangeData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveChanges = async () => {
    const changeAmountNum = parseFloat(changeData.changeAmount);
    if (
      !changeData.comment.trim() ||
      !changeData.changeAmount.trim() ||
      isNaN(changeAmountNum) ||
      changeAmountNum <= 0
    ) {
      toast.error(
        "Please fill in a comment and a valid positive change amount."
      );
      return;
    }

    let newTotalValue;
    if (changeType === "increase") {
      newTotalValue = latestProjectValue + changeAmountNum;
    } else {
      if (changeAmountNum > latestProjectValue) {
        toast.error(
          "Deduction amount cannot be greater than the current project value."
        );
        return;
      }
      newTotalValue = latestProjectValue - changeAmountNum;
    }

    const entryDataPayload = {
      entry_date: new Date().toISOString(),
      comment: changeData.comment,
      amount: newTotalValue,
    };

    const success = await addProjectValueEntry(projectId, entryDataPayload);
    if (success) {
      setIsChangeModalOpen(false);
    }
  };

  return (
    <>
      <div
        className="p-3 lg:p-4 bg-white shadow-md rounded-lg overflow-x-auto"
        style={{ borderRadius: "12px" }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h2 className="text-lg lg:text-xl font-semibold">
            Project Value History
          </h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => handleOpenChangeModal("increase")}
              disabled={isValueEntrySubmitting || isLatestEntryPending}
              className="flex-1 sm:flex-none bg-green-500 text-white px-2 lg:px-3 py-1.5 rounded-md text-xs font-medium flex items-center justify-center hover:bg-green-600 transition-colors disabled:opacity-60"
            >
              <PlusCircle size={12} className="mr-1 lg:mr-1.5" />
              <span className="hidden sm:inline">Increase Value</span>
              <span className="sm:hidden">Increase</span>
            </button>
            <button
              onClick={() => handleOpenChangeModal("deduct")}
              disabled={
                isValueEntrySubmitting ||
                latestProjectValue <= 0 ||
                isLatestEntryPending
              }
              className="flex-1 sm:flex-none bg-red-500 text-white px-2 lg:px-3 py-1.5 rounded-md text-xs font-medium flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-60"
            >
              <MinusCircle size={12} className="mr-1 lg:mr-1.5" />
              <span className="hidden sm:inline">Deduct Value</span>
              <span className="sm:hidden">Deduct</span>
            </button>
          </div>
        </div>

        {isLatestEntryPending && (
          <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 text-yellow-700 text-xs rounded-md text-center">
            A project value change is currently pending customer approval. You
            cannot make new changes until the current one is resolved.
          </div>
        )}

        {isValueEntriesLoading && projectValueEntries.length === 0 ? (
          <div className="flex justify-center items-center py-10">
            <AiOutlineLoading3Quarters className="animate-spin text-2xl text-blue-500" />
            <p className="ml-2 text-gray-500 text-sm">
              Loading project value history...
            </p>
          </div>
        ) : projectValueEntries.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">
            No project value history found. Add the current project value.
          </div>
        ) : (
          <>
            {/* Desktop Table - Hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse text-xs min-w-[600px]">
                <thead>
                  <tr className="text-left border-b border-gray-300 bg-gray-50">
                    <th className="p-2 lg:p-2.5">Date Recorded</th>
                    <th className="p-2 lg:p-2.5">Comments</th>
                    <th className="p-2 lg:p-2.5 text-center">Status</th>
                    <th className="p-2 lg:p-2.5 text-center">Change (INR)</th>
                    <th className="p-2 lg:p-2.5 text-right">
                      Updated Value (INR)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projectValueEntries.map((project, index) => {
                  // --- MODIFICATION START: Corrected Change Calculation ---
                  const previousApprovedEntry = projectValueEntries
                    .slice(index + 1)
                    .find((entry) => entry.status === "Approved");

                  const isFirstApprovedEntry =
                    project.status === "Approved" && !previousApprovedEntry;

                  let change = null;
                  if (
                    project.status === "Approved" ||
                    project.status === "Pending"
                  ) {
                    if (previousApprovedEntry) {
                      change = project.amount - previousApprovedEntry.amount;
                    } else {
                      change = project.amount;
                    }
                  }
                  // --- MODIFICATION END ---

                  const statusInfo = statusDisplayInfo(project.status);

                  return (
                    <tr
                      key={project.id}
                      className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 whitespace-nowrap text-xs">
                        {formatDisplayDate(project.entry_date)}
                      </td>
                      <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 max-w-[120px] lg:max-w-xs">
                        <div className="flex items-center">
                          <span className="truncate mr-1 lg:mr-2 text-xs">
                            {project.comment}
                          </span>
                         
                        </div>
                      </td>
                      <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 text-center">
                        <span
                          className={`inline-block px-1.5 lg:px-2 py-0.5 lg:py-1 font-semibold rounded-full text-[9px] lg:text-[10px] ${statusInfo.bg} ${statusInfo.text}`}
                        >
                          {project.status || "N/A"}
                        </span>
                      </td>
                      <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 text-center">
                        {isFirstApprovedEntry ? (
                          <span className="text-gray-500 font-medium text-xs">
                            Initial Value
                          </span>
                        ) : change !== null ? (
                          change > 0 ? (
                            <span className="text-green-600 font-semibold flex items-center justify-center gap-0.5 lg:gap-1 text-xs">
                              <ArrowUp size={10} className="lg:w-3 lg:h-3" /> +{" "}
                              {formatCurrency(change)}
                            </span>
                          ) : change < 0 ? (
                            <span className="text-red-600 font-semibold flex items-center justify-center gap-0.5 lg:gap-1 text-xs">
                              <ArrowDown size={10} className="lg:w-3 lg:h-3" />{" "}
                              - {formatCurrency(Math.abs(change))}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs">-</span>
                          )
                        ) : (
                          <span className="text-gray-500 text-xs italic">
                            (No Change)
                          </span>
                        )}
                      </td>
                      <td className="py-2 lg:py-2.5 px-2 lg:px-2.5 whitespace-nowrap text-gray-800 text-right font-medium text-xs">
                        {formatCurrency(project.amount)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-bold border-t-2 border-gray-300 bg-gray-50">
                    <td
                      colSpan="4"
                      className="p-2 lg:p-2.5 text-right text-sm lg:text-base"
                    >
                      Latest Approved Project Value
                    </td>
                    <td className="p-2 lg:p-2.5 text-gray-800 text-right text-sm lg:text-base font-bold">
                      {formatCurrency(latestProjectValue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile Card View - Shown only on mobile */}
            <div className="md:hidden space-y-3">
              {projectValueEntries.map((project, index) => {
                const previousApprovedEntry = projectValueEntries
                  .slice(index + 1)
                  .find((entry) => entry.status === "Approved");

                const isFirstApprovedEntry =
                  project.status === "Approved" && !previousApprovedEntry;

                let change = null;
                if (
                  project.status === "Approved" ||
                  project.status === "Pending"
                ) {
                  if (previousApprovedEntry) {
                    change = project.amount - previousApprovedEntry.amount;
                  } else {
                    change = project.amount;
                  }
                }

                const statusInfo = statusDisplayInfo(project.status);

                return (
                  <div
                    key={project.id}
                    className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
                  >
                    {/* Date */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-xs text-gray-600">
                        {formatDisplayDate(project.entry_date)}
                      </div>
                      <span
                        className={`inline-block px-2 py-0.5 font-semibold rounded-full text-[9px] ${statusInfo.bg} ${statusInfo.text}`}
                      >
                        {project.status || "N/A"}
                      </span>
                    </div>

                    {/* Comment */}
                    <div className="mb-2">
                      <div className="flex items-start justify-between">
                        <span className="text-xs text-gray-800 flex-1 mr-2">
                          {project.comment}
                        </span>
                        <button
                          onClick={() => handleViewComment(project.comment)}
                          className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                        >
                          <FaEye size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Change & Value */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <div className="text-xs">
                        <span className="text-gray-500 block mb-1">Change:</span>
                        {isFirstApprovedEntry ? (
                          <span className="text-gray-500 font-medium">
                            Initial Value
                          </span>
                        ) : change !== null ? (
                          change > 0 ? (
                            <span className="text-green-600 font-semibold flex items-center gap-0.5">
                              <ArrowUp size={10} /> + {formatCurrency(change)}
                            </span>
                          ) : change < 0 ? (
                            <span className="text-red-600 font-semibold flex items-center gap-0.5">
                              <ArrowDown size={10} /> - {formatCurrency(Math.abs(change))}
                            </span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )
                        ) : (
                          <span className="text-gray-500 italic text-xs">
                            (No Change)
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-gray-500 block mb-1 text-xs">Value:</span>
                        <span className="font-semibold text-gray-900 text-sm">
                          ₹{formatCurrency(project.amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Latest Value Footer for Mobile */}
              <div className="bg-gray-50 rounded-lg border-2 border-gray-300 p-3 font-bold">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-700">Latest Approved Project Value</span>
                  <span className="text-sm text-gray-800">
                    ₹{formatCurrency(latestProjectValue)}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <GenericModal
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        title="Full Comment"
      >
        <p className="whitespace-pre-wrap break-words">{selectedComment}</p>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setIsCommentModalOpen(false)}
            className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-xs font-medium"
          >
            Close
          </button>
        </div>
      </GenericModal>

      <GenericModal
        isOpen={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        title={`${
          changeType === "increase" ? "Increase" : "Deduct"
        } Project Value`}
        size="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Total Project Value:{" "}
            <span className="font-semibold text-gray-700">
              ₹{formatCurrency(latestProjectValue)}
            </span>
          </p>
          <div>
            <label
              htmlFor="changeAmount"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Amount to {changeType} (INR){" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="changeAmount"
              name="changeAmount"
              value={changeData.changeAmount}
              onChange={handleNewChange}
              placeholder={`Enter amount to ${changeType}`}
              className="w-full p-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              disabled={isValueEntrySubmitting}
            />
          </div>
          <div>
            <label
              htmlFor="comment"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Comment / Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="comment"
              name="comment"
              value={changeData.comment}
              onChange={handleNewChange}
              placeholder={`Reason for this value ${
                changeType === "increase" ? "increase" : "deduction"
              }`}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
              disabled={isValueEntrySubmitting}
            />
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
            <button
              onClick={() => setIsChangeModalOpen(false)}
              disabled={isValueEntrySubmitting}
              className="px-4 py-1.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={
                isValueEntrySubmitting ||
                !changeData.changeAmount.trim() ||
                !changeData.comment.trim()
              }
              className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
            >
              {isValueEntrySubmitting ? (
                <AiOutlineLoading3Quarters className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              Save Change
            </button>
          </div>
        </div>
      </GenericModal>
    </>
  );
};

export default PaymentValue;
